import React, { useState } from 'react';
import {
  createAlertSetting,
  validateApiKey,
  CreateAlertSettingRequest,
} from '../services/alertService';

interface AlertSetupWizardProps {
  onComplete: () => void;
  onCancel: () => void;
}

type Step = 'api-key' | 'budget' | 'notifications' | 'summary';

const AlertSetupWizard: React.FC<AlertSetupWizardProps> = ({
  onComplete,
  onCancel,
}) => {
  const [currentStep, setCurrentStep] = useState<Step>('api-key');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [validating, setValidating] = useState(false);
  const [isValidated, setIsValidated] = useState(false);

  const [formData, setFormData] = useState<CreateAlertSettingRequest>({
    name: '',
    provider: 'openai',
    apiKey: '',
    budgetLimit: 100,
    budgetPeriod: 'monthly',
    thresholdPercentage: 80,
    notificationEmail: '',
    webhookUrl: '',
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'budgetLimit' || name === 'thresholdPercentage' 
        ? parseFloat(value) || 0 
        : value,
    }));
    setError('');
    if (name === 'provider' || name === 'apiKey') {
      setIsValidated(false);
    }
  };

  const handleValidateApiKey = async () => {
    if (!formData.apiKey.trim()) {
      setError('Please enter an admin API key');
      return;
    }

    setValidating(true);
    setError('');

    try {
      const response = await validateApiKey({
        provider: formData.provider,
        apiKey: formData.apiKey,
      });

      if (response.isValid) {
        setIsValidated(true);
      } else {
        setError('Invalid admin key. Please check and try again.');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to validate admin key.');
    } finally {
      setValidating(false);
    }
  };

  const handleNext = async () => {
    setError('');

    switch (currentStep) {
      case 'api-key':
        if (!formData.provider) {
          setError('Please select a provider');
          return;
        }
        if (!formData.apiKey.trim()) {
          setError('Please enter an admin API key');
          return;
        }
        if (!isValidated) {
          setError('Please validate your admin key first');
          return;
        }
        setCurrentStep('budget');
        break;

      case 'budget':
        if (!formData.name.trim()) {
          setError('Please enter an alert name');
          return;
        }
        if (formData.budgetLimit <= 0) {
          setError('Budget limit must be greater than 0');
          return;
        }
        if (formData.thresholdPercentage <= 0 || formData.thresholdPercentage > 100) {
          setError('Threshold must be between 1 and 100');
          return;
        }
        setCurrentStep('notifications');
        break;

      case 'notifications':
        if (!formData.notificationEmail.trim()) {
          setError('Please enter a notification email');
          return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.notificationEmail)) {
          setError('Please enter a valid email address');
          return;
        }
        setCurrentStep('summary');
        break;

      case 'summary':
        await handleSubmit();
        break;
    }
  };

  const handleBack = () => {
    setError('');
    const steps: Step[] = ['api-key', 'budget', 'notifications', 'summary'];
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1]);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      await createAlertSetting(formData);
      onComplete();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create alert setting');
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => {
    const steps: { id: Step; label: string }[] = [
      { id: 'api-key', label: 'Admin Key' },
      { id: 'budget', label: 'Budget' },
      { id: 'notifications', label: 'Notifications' },
      { id: 'summary', label: 'Summary' },
    ];

    return (
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const isActive = step.id === currentStep;
            const isCompleted = steps.findIndex((s) => s.id === currentStep) > index;

            return (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                      isActive
                        ? 'bg-indigo-600 text-white'
                        : isCompleted
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {isCompleted ? '✓' : index + 1}
                  </div>
                  <span className="mt-2 text-sm font-medium text-gray-700">
                    {step.label}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-4 ${
                      isCompleted ? 'bg-green-500' : 'bg-gray-200'
                    }`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  };

  const renderApiKeyStep = () => (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-gray-900">Admin Key Configuration</h2>
      <p className="text-gray-600">
        Select your AI provider and enter your admin API key. We only need <strong>read access</strong> to monitor usage and costs.
      </p>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Provider
        </label>
        <select
          name="provider"
          value={formData.provider}
          onChange={handleInputChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        >
          <option value="openai">OpenAI</option>
        </select>
        <p className="mt-2 text-sm text-indigo-600">
          Support for Anthropic Claude and other providers coming soon!
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Admin API Key
        </label>
        <input
          type="password"
          name="apiKey"
          value={formData.apiKey}
          onChange={handleInputChange}
          placeholder="sk-..."
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
        <div className="mt-2 space-y-2">
          <p className="text-sm text-gray-600">
            <strong>Create your admin key:</strong>{' '}
            <a
              href="https://platform.openai.com/settings/organization/admin-keys"
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:text-indigo-800 underline"
            >
              OpenAI API Keys Dashboard
            </a>
          </p>
          <p className="text-sm text-amber-600">
            ⚠️ Only <strong>read access</strong> is required. We only monitor usage and costs.
          </p>
        </div>
        <button
          type="button"
          onClick={handleValidateApiKey}
          disabled={validating || !formData.apiKey.trim()}
          className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          {validating ? 'Validating...' : isValidated ? '✓ Validated' : 'Validate Admin Key'}
        </button>
      </div>
    </div>
  );

  const renderBudgetStep = () => (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-gray-900">Budget Settings</h2>
      <p className="text-gray-600">
        Configure your spending limits and alert thresholds.
      </p>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Alert Name
        </label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          placeholder="My Production Alert"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Budget Period
        </label>
        <select
          name="budgetPeriod"
          value={formData.budgetPeriod}
          onChange={handleInputChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        >
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Budget Limit (USD)
        </label>
        <input
          type="number"
          name="budgetLimit"
          value={formData.budgetLimit}
          onChange={handleInputChange}
          min="1"
          step="0.01"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Alert Threshold (%)
        </label>
        <input
          type="number"
          name="thresholdPercentage"
          value={formData.thresholdPercentage}
          onChange={handleInputChange}
          min="1"
          max="100"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
        <p className="mt-1 text-sm text-gray-500">
          You'll receive an alert when spending reaches {formData.thresholdPercentage}% of your budget
        </p>
      </div>
    </div>
  );

  const renderNotificationsStep = () => (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-gray-900">Notification Settings</h2>
      <p className="text-gray-600">
        Choose how you want to receive alerts.
      </p>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Email Address *
        </label>
        <input
          type="email"
          name="notificationEmail"
          value={formData.notificationEmail}
          onChange={handleInputChange}
          placeholder="you@example.com"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Webhook URL (Optional)
        </label>
        <input
          type="url"
          name="webhookUrl"
          value={formData.webhookUrl}
          onChange={handleInputChange}
          placeholder="https://your-webhook.com/endpoint"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
        <p className="mt-1 text-sm text-gray-500">
          Optional: Receive alerts via webhook for integration with Slack, Discord, etc.
        </p>
      </div>
    </div>
  );

  const renderSummaryStep = () => (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-gray-900">Summary</h2>
      <p className="text-gray-600">
        Review your alert configuration before submitting.
      </p>

      <div className="bg-gray-50 rounded-lg p-6 space-y-3">
        <div className="flex justify-between">
          <span className="font-medium text-gray-700">Alert Name:</span>
          <span className="text-gray-900">{formData.name}</span>
        </div>
        <div className="flex justify-between">
          <span className="font-medium text-gray-700">Provider:</span>
          <span className="text-gray-900 capitalize">{formData.provider}</span>
        </div>
        <div className="flex justify-between">
          <span className="font-medium text-gray-700">Budget:</span>
          <span className="text-gray-900">
            ${formData.budgetLimit.toFixed(2)} / {formData.budgetPeriod}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="font-medium text-gray-700">Alert Threshold:</span>
          <span className="text-gray-900">{formData.thresholdPercentage}%</span>
        </div>
        <div className="flex justify-between">
          <span className="font-medium text-gray-700">Email:</span>
          <span className="text-gray-900">{formData.notificationEmail}</span>
        </div>
        {formData.webhookUrl && (
          <div className="flex justify-between">
            <span className="font-medium text-gray-700">Webhook:</span>
            <span className="text-gray-900 truncate ml-4">{formData.webhookUrl}</span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-lg p-8">
      {renderStepIndicator()}

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      <div className="mb-8">
        {currentStep === 'api-key' && renderApiKeyStep()}
        {currentStep === 'budget' && renderBudgetStep()}
        {currentStep === 'notifications' && renderNotificationsStep()}
        {currentStep === 'summary' && renderSummaryStep()}
      </div>

      <div className="flex justify-between">
        <button
          type="button"
          onClick={currentStep === 'api-key' ? onCancel : handleBack}
          disabled={loading}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {currentStep === 'api-key' ? 'Cancel' : 'Back'}
        </button>

        <button
          type="button"
          onClick={handleNext}
          disabled={loading}
          className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading
            ? 'Creating...'
            : currentStep === 'summary'
            ? 'Create Alert'
            : 'Next'}
        </button>
      </div>
    </div>
  );
};

export default AlertSetupWizard;
